<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="emitUpdateModelValue"
    max-width="860"
  >
    <v-card>
      <v-card-title class="text-h6 mt-4">Solicitar Cotação</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <p class="mb-2">
              Descreva aqui o produto que você deseja criar, com o máximo de
              detalhes possível. Caso tenha alguma foto que possa servir de
              referência, anexe-as abaixo. Obs: Produtos mais complexos terão um
              orçamento mais elevado, produtos mais simples deverão ter um
              orçamento mais barato.
            </p>
            <div
              class="support-callout pa-4 mb-4 rounded-lg d-flex align-center justify-space-between"
            >
              <v-row>
                <v-col cols="12" md="8">
                  <div class="d-flex align-center">
                    <v-avatar size="40" class="mr-3" color="#e8f5e9">
                      <v-icon color="#25D366">mdi-whatsapp</v-icon>
                    </v-avatar>

                    <div>
                      <div class="text-subtitle-1 font-weight-medium">
                        Precisa de suporte humano?
                      </div>
                      <div class="text-body-2 text-medium-emphasis">
                        Fale com nossa equipe no WhatsApp e receba atendimento.
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn
                    :href="whatsLink"
                    target="_blank"
                    rel="noopener"
                    :disabled="!sanitizedSupportWhats || loading"
                    prepend-icon="mdi-whatsapp"
                    class="ml-4"
                    :style="{
                      backgroundColor: '#25D366',
                      color: '#fff',
                      fontWeight: 600,
                    }"
                  >
                    Iniciar conversa
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="quotationContact"
              variant="outlined"
              type="email"
              label="Seu email de contato"
              :rules="[rules.requiredEmail]"
              placeholder="nome@exemplo.com"
            />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field
              append-inner-icon="mdi-whatsapp"
              v-model="quotationPhone"
              label="WhatsApp (Opcional)"
              placeholder="(00) 00000-0000"
              v-mask="'(##) #####-####'"
              variant="outlined"
            />
          </v-col>

          <!-- Descrição -->
          <v-col cols="12" md="12">
            <v-textarea
              v-model="quotationText"
              variant="outlined"
              label="Descrição do produto"
              placeholder="Exemplos de produtos: um porta canetas com formato de um motor elétrico, um boneco modelado a partir do mascote da sua empresa, uma engrenagem para uma máquina industrial, um cofrinho para guardar moedas com formato de um porquinho cor de rosa, com asas brancas."
              rows="5"
              :maxlength="MAX_DESC"
              counter
              :rules="[rules.required, rules.maxDesc]"
            />
          </v-col>
          <!-- Input de imagens -->
          <v-col cols="12" md="12">
            <v-file-input
              v-model="newFiles"
              variant="outlined"
              label="Selecionar imagens"
              prepend-icon="mdi-camera"
              multiple
              accept="image/*"
              show-size
              counter
              @change="handleFileInput"
            />
          </v-col>

          <v-col cols="12">
            <!-- Galeria de imagens com botão de remover -->
            <v-row
              v-if="images.length"
              class="d-flex flex-wrap"
              style="gap: 16px"
            >
              <v-col
                v-for="(img, i) in images"
                :key="i"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                class="position-relative"
              >
                <v-btn
                  icon
                  size="x-small"
                  color="red"
                  class="remove-btn"
                  @click="removeImage(i)"
                  :aria-label="`Remover imagem ${i + 1}`"
                >
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>

                <v-img
                  :src="getFilePreview(img)"
                  aspect-ratio="1"
                  class="rounded elevation-2"
                  cover
                  alt="Imagem de referência"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="py-5">
        <v-btn text @click="closeDialog">Cancelar</v-btn>
        <v-btn
          color="secondary"
          @click="submitQuotation"
          :loading="loading"
          :disabled="loading || !quotationText?.trim() || !isValidEmail"
        >
          Enviar Cotação
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import {
  createQuotationFormData,
  submitQuotationRequest,
} from "@/services/api";

export default {
  name: "QuotationDialog",
  props: {
    modelValue: { type: Boolean, required: true },
  },
  emits: ["update:modelValue", "success", "error"],
  data() {
    return {
      quotationText: "",
      quotationContact: "",
      quotationPhone: "",
      newFiles: [],
      images: /** @type {File[]} */ ([]),
      objectUrls: [],
      loading: false,
      snackbar: { show: false, message: "", color: "success" },
      MAX_DESC: 3000,
      supportWhats: "53999225453",
      rules: {
        required: (v) => !!(v && String(v).trim()) || "Campo obrigatório",
        requiredEmail: (v) => {
          const email = (v || "").trim();
          if (!email) return "Informe seu email de contato";
          const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return re.test(email) || "E-mail inválido";
        },
        maxDesc: (v) =>
          String(v || "").length <= this.MAX_DESC ||
          `Máximo de ${this.MAX_DESC} caracteres`,
      },
    };
  },
  computed: {
    isValidEmail() {
      const email = (this.quotationContact || "").trim();
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !!email && re.test(email);
    },
    sanitizedSupportWhats() {
      return (this.supportWhats || "").replace(/\D/g, "");
    },
    whatsMessage() {
      const desc = (this.quotationText || "").trim();
      const preview = desc.length > 300 ? desc.slice(0, 300) + "…" : desc;
      const lines = [
        "Olá! Gostaria de falar sobre uma cotação.",
        preview ? `Descrição: ${preview}` : null,
        this.quotationContact ? `E-mail: ${this.quotationContact}` : null,
      ].filter(Boolean);
      return lines.join("\n");
    },
    whatsLink() {
      if (!this.sanitizedSupportWhats) return null;
      const msg = encodeURIComponent(this.whatsMessage);
      return `https://wa.me/${this.sanitizedSupportWhats}?text=${msg}`;
    },
  },
  methods: {
    emitUpdateModelValue(v) {
      this.$emit("update:modelValue", v);
    },
    closeDialog() {
      this.emitUpdateModelValue(false);
    },
    notify(message, color = "success") {
      this.snackbar = { show: true, message, color };
    },
    handleFileInput() {
      if (!this.newFiles) return;
      const asArray = Array.isArray(this.newFiles)
        ? this.newFiles
        : [this.newFiles];
      const onlyImages = asArray.filter((f) => f?.type?.startsWith("image/"));
      const rejected = asArray.length - onlyImages.length;
      if (rejected > 0)
        this.notify(
          `${rejected} arquivo(s) ignorado(s) por não serem imagens.`,
          "warning"
        );
      this.images = [...this.images, ...onlyImages];
      this.newFiles = [];
    },
    removeImage(index) {
      const url = this.objectUrls[index];
      if (url) URL.revokeObjectURL(url);
      this.images.splice(index, 1);
      this.objectUrls.splice(index, 1);
    },
    getFilePreview(file) {
      const idx = this.images.indexOf(file);
      if (idx > -1 && this.objectUrls[idx]) return this.objectUrls[idx];
      const url = URL.createObjectURL(file);
      if (idx > -1) this.objectUrls[idx] = url;
      return url;
    },
    async submitQuotation() {
      if (!this.quotationText?.trim()) {
        this.notify("Informe a descrição da cotação.", "error");
        return;
      }
      if (!this.isValidEmail) {
        this.notify("E-mail inválido.", "error");
        return;
      }

      this.loading = true;
      try {
        const fd = createQuotationFormData({
          description: this.quotationText.trim(),
          contactEmail: this.quotationContact?.trim() || undefined,
          contactWhats: this.quotationPhone?.replace(/\D/g, "") || undefined,
          files: this.images,
        });

        const { data } = await submitQuotationRequest(fd);

        if (data?.status === "success") {
          this.$emit("success", "Cotação enviada com sucesso!");
          this.resetFields();
          this.closeDialog();
        } else {
          throw new Error(data?.message || "Erro ao enviar cotação.");
        }
      } catch (err) {
        console.error("[QuotationDialog] submitQuotation error:", err);
        this.$emit("error", "Erro ao enviar cotação. Tente novamente.");
        this.notify("Erro ao enviar cotação.", "error");
      } finally {
        this.loading = false;
      }
    },
    resetFields() {
      for (const url of this.objectUrls) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.error(e);
        }
      }
      this.objectUrls = [];
      this.images = [];
      this.newFiles = [];
      this.quotationText = "";
      this.quotationContact = "";
      this.quotationPhone = "";
    },
  },
  beforeUnmount() {
    for (const url of this.objectUrls) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
      }
    }
  },
};
</script>

<style scoped>
.remove-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.85);
}
.support-callout {
  margin-top: 20px;
  border: 1px solid #e6f7ed;
  box-shadow: 0 2px 10px rgba(37, 211, 102, 0.06);
}
</style>
